import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usePagesStore } from '../stores/pagesStore';
import { useDialogStore } from '../stores/dialogStore';
import ParagraphEditor from '../components/ParagraphEditor';

const PersonalPageEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken, profile } = useAuthStore();
  const { isLoading } = usePagesStore();
  const { alert, confirm } = useDialogStore();
  const [pageData, setPageData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        // Fetch page by ID
        const response = await fetch(`/api/pages/${id}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        const data = await response.json();

        if (data.success && data.page) {
          // Verify ownership
          if (data.page.profile_id !== profile?.id) {
            setError('You do not have permission to edit this page');
            return;
          }

          // If page has no draft but has published revision, ask to create draft
          if (!data.page.draft_revision_id && data.page.published_revision_id) {
            const confirmed = await confirm(
              'Create Draft',
              'This page is published. Do you want to create a new draft to edit?'
            );

            if (!confirmed) {
              navigate('/profile/dashboard');
              return;
            }

            // Create draft from published
            const createDraftResponse = await fetch(`/api/pages/${id}/revisions/create-draft`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`
              }
            });

            const createDraftData = await createDraftResponse.json();

            if (createDraftData.success) {
              // Refresh page data to get the new draft
              const refreshResponse = await fetch(`/api/pages/${id}`, {
                headers: {
                  'Authorization': `Bearer ${accessToken}`
                }
              });

              const refreshData = await refreshResponse.json();
              if (refreshData.success) {
                setPageData(refreshData.page);
              }
            } else {
              setError(createDraftData.message || 'Failed to create draft');
              return;
            }
          } else {
            setPageData(data.page);
          }
        } else {
          setError(data.message || 'Page not found');
        }
      } catch (err) {
        console.error('Failed to load page:', err);
        setError('Failed to load page');
      }
    };

    if (accessToken && id && profile) {
      fetchPageData();
    }
  }, [id, accessToken, profile, navigate]);

  const handleClose = () => {
    // Navigate to the page that was being edited
    if (pageData && pageData.slug) {
      navigate(`/profile/page/${pageData.slug}`);
    } else {
      navigate('/profile/dashboard');
    }
  };

  const handlePublish = async () => {
    if (!pageData || !pageData.draft_revision_id) return;

    const confirmed = await confirm(
      'Publish Page',
      'Are you sure you want to publish this page? It will be visible to all members.'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`/api/pages/${pageData.id}/revisions/${pageData.draft_revision_id}/publish`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        await alert('Success', 'Page published successfully');
        // Reload to refresh sidebar menu and view the published page
        window.location.href = `/profile/page/${pageData.slug}`;
      } else {
        alert('Error', data.message || 'Failed to publish page');
      }
    } catch (err) {
      console.error('Failed to publish:', err);
      alert('Error', 'Failed to publish page');
    }
  };

  const handleDiscard = async () => {
    if (!pageData || !pageData.draft_revision_id) return;

    const confirmed = await confirm(
      'Discard Draft',
      'Are you sure you want to discard this draft? This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`/api/pages/${pageData.id}/revisions/${pageData.draft_revision_id}/discard`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        navigate('/profile/dashboard');
      } else {
        alert('Error', data.message || 'Failed to discard draft');
      }
    } catch (err) {
      console.error('Failed to discard:', err);
      alert('Error', 'Failed to discard draft');
    }
  };

  if (error) {
    return (
      <div className="surface rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-primary mb-4">Error</h2>
        <p className="text-secondary">{error}</p>
        <button
          onClick={() => navigate('/profile/dashboard')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="surface rounded-lg p-8 text-center">
        <p className="text-secondary">Loading page...</p>
      </div>
    );
  }

  return (
    <div className="surface rounded-lg shadow-lg p-6">
      <ParagraphEditor
        pageId={pageData.id}
        revisionId={pageData.draft_revision_id || pageData.published_revision_id}
        onClose={handleClose}
        onPublish={pageData.draft_revision_id ? handlePublish : null}
        onDiscard={pageData.draft_revision_id ? handleDiscard : null}
        isDraft={!!pageData.draft_revision_id}
      />
    </div>
  );
};

export default PersonalPageEditor;
